import { Select, Skeleton, Tooltip } from "antd";
import React from "react";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import type { ScheduleSlot } from "../../service/schedulePick.service";
import { EventType, trackEvent } from "../../utils/analytics";
import { resolveClinicZone, scheduleSlotLabel } from "../../utils/clientSchedule";
import { VISIBLE_SLOTS } from "./quickStart";
import { ChevronDownIcon, ChevronUpIcon, ClockIcon, CloseIcon, TemplateIcon, ZapIcon } from "./quickStartIcons";
import styles from "./quickstart.module.scss";
import type { QuickStartSchedule, SessionTemplateState } from "./useQuickStart";

/** "Template  Family Medicine SOAP ⌄": the template every Start on the card will use, and its picker. */
export const TemplatePicker = ({ session }: { session: SessionTemplateState }) => {
  const { template } = session;
  // The hint must not sit over the open list of templates.
  const [pickerOpen, setPickerOpen] = React.useState(false);
  if (!session.ready) {
    return <Skeleton.Button active block className={styles.Template__loading} />;
  }
  return (
    <Tooltip
      title={template ? null : "No template on file yet. Start now and choose one when you submit, or pick it here."}
      open={pickerOpen ? false : undefined}
    >
      <Select
        variant="borderless"
        showSearch
        data-testid="quick-start-template"
        className={`${styles.Template} ${template ? "" : styles["Template--pending"]}`}
        popupMatchSelectWidth
        prefix={
          <span className={styles.Template__prefix}>
            <TemplateIcon />
            <span className={styles.Template__label}>Template</span>
          </span>
        }
        suffixIcon={<ChevronDownIcon />}
        value={template?.value}
        placeholder="Choose"
        options={session.groups}
        optionFilterProp="searchText"
        labelRender={() => template?.name ?? "Choose"}
        onOpenChange={(open) => {
          setPickerOpen(open);
          if (open) session.loadGallery();
        }}
        onChange={(value) => {
          const option = session.options.find((candidate) => candidate.value === value);
          if (option) session.choose(option);
        }}
      />
    </Tooltip>
  );
};

type QuickStartCardProps = {
  schedule: QuickStartSchedule;
  session: SessionTemplateState;
  /** The patient whose recording is starting, while it starts. */
  startingId: number | null;
  onQuickStart: (slot: ScheduleSlot, context: { expanded: boolean }) => void;
  /** × : the schedule's dismiss, wrapped by the caller so it can move focus. */
  onDismiss: () => void;
  dismissRef?: React.Ref<HTMLButtonElement>;
  footer: React.ReactNode;
};

/**
 * "Who's in the room?": the whole of today's schedule in appointment order, each patient one click
 * from recording with the template shown under the list. It opens expanded, scrolled so the Now
 * line sits mid-list: a clinician running late picks an earlier appointment a scroll up, without ×.
 * "Show fewer" narrows it to three rows around now. × closes it and the card becomes Start Encounter.
 */
const QuickStartCard = ({
  schedule,
  session,
  startingId,
  onQuickStart,
  onDismiss,
  dismissRef,
  footer,
}: QuickStartCardProps) => {
  const { sessionTerm, sessionTermLower } = useSpecialityReference();
  const zone = React.useMemo(() => resolveClinicZone(schedule.pick?.clinic_tz), [schedule.pick?.clinic_tz]);
  const [expanded, setExpanded] = React.useState(true);
  // Each Start is described by its row's time and provider: two patients can share a name.
  const rowId = React.useId();
  // The expanded list scrolls past ~5 rows; a fade marks that there is more above or below.
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = React.useState({ scrollable: false, atStart: true, atEnd: true });
  const measure = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const next = {
      scrollable: el.scrollHeight > el.clientHeight + 1,
      atStart: el.scrollTop <= 8,
      atEnd: el.scrollTop + el.clientHeight >= el.scrollHeight - 8,
    };
    setScroll((prev) =>
      prev.scrollable === next.scrollable && prev.atStart === next.atStart && prev.atEnd === next.atEnd ? prev : next,
    );
  }, []);

  const { view } = schedule;
  const slots = view.kind === "list" ? view.slots : [];
  const first = view.kind === "list" ? view.first : 0;
  const nowAt = view.kind === "list" ? view.now : 0;
  const more = slots.length - VISIBLE_SLOTS;
  const open = expanded && more > 0;

  // Centre the Now line when the list opens and as the clock moves it, until the clinician scrolls
  // the list themselves; collapsing and expanding again re-centres.
  const userScrolled = React.useRef(false);
  const autoScrolling = React.useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-centre when the list opens or "now" moves
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!open) userScrolled.current = false;
    if (!el || !open || userScrolled.current) return;
    const line = el.querySelector<HTMLElement>("[data-now-line]");
    if (!line) return;
    const max = el.scrollHeight - el.clientHeight;
    const target = Math.round(Math.min(Math.max(line.offsetTop + line.offsetHeight / 2 - el.clientHeight / 2, 0), max));
    if (Math.abs(target - el.scrollTop) < 1) return;
    autoScrolling.current = true;
    el.scrollTop = target;
  }, [open, nowAt, slots.length]);
  const onScroll = () => {
    if (autoScrolling.current) autoScrolling.current = false;
    else userScrolled.current = true;
    measure();
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the rows change
  React.useLayoutEffect(measure, [measure, open, slots.length]);

  if (view.kind !== "list") return null;
  const full = open || more <= 0;
  const rows = full ? slots : slots.slice(first, first + VISIBLE_SLOTS);
  // A colleague's patient names its provider; once any row does, every row does, so the list reads evenly.
  const namesProviders = slots.some((slot) => slot.client.is_owned === false);
  const busy = startingId !== null || !session.ready;
  // Between the last appointment already due and the first one still ahead.
  const nowLine = (
    <div className={styles.Room__now} data-now-line data-testid="quick-start-now">
      <span className={styles.Room__nowLabel}>Now</span>
    </div>
  );

  const toggle = () => {
    if (!open) trackEvent(EventType.QUICK_START_LIST_EXPANDED, { candidates: slots.length });
    setExpanded(!open);
  };

  return (
    <div className={styles.Card} data-testid="quick-start-card">
      <div className={styles.Room__top}>
        <div className={styles.Room__chip} data-testid="quick-start-count">
          <ClockIcon />
          {slots.length} booked today
        </div>
        <Tooltip title={`None of these? Close the list and use Start ${sessionTerm}`} trigger={["hover", "focus"]}>
          <button
            type="button"
            ref={dismissRef}
            aria-label="Hide schedule suggestions"
            className={styles.Room__dismiss}
            disabled={startingId !== null}
            onClick={onDismiss}
            data-testid="quick-start-dismiss"
          >
            <CloseIcon />
          </button>
        </Tooltip>
      </div>

      <div className={styles.Room__title} data-testid="quick-start-title">
        Who's in the room?
      </div>

      <div className={styles.Room__list}>
        <div className={styles.Room__scrollWrap}>
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className={[
              styles.Room__rows,
              open ? styles["Room__rows--expanded"] : "",
              open && scroll.scrollable ? styles["Room__rows--scrollable"] : "",
            ].join(" ")}
            data-testid="quick-start-candidates"
          >
            {rows.map((slot, index) => {
              const time = scheduleSlotLabel(slot.scheduled_at, zone);
              const provider = namesProviders ? slot.client.owner_name : null;
              return (
                <React.Fragment key={slot.client.id}>
                  {full && index === nowAt && nowLine}
                  <div className={styles.Room__row} data-testid="quick-start-candidate">
                    <div className={styles.Room__text}>
                      <div className={styles.Room__name} title={slot.client.name}>
                        {slot.client.name}
                      </div>
                      <div className={styles.Room__meta} id={`${rowId}-${slot.client.id}`}>
                        {[time, provider].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles.Room__start}
                      aria-label={`Start ${sessionTermLower} with ${slot.client.name}`}
                      aria-describedby={`${rowId}-${slot.client.id}`}
                      disabled={busy}
                      onClick={() => onQuickStart(slot, { expanded: open })}
                      data-testid="quick-start-start"
                    >
                      <ZapIcon />
                      {startingId === slot.client.id ? "Starting…" : "Start"}
                    </button>
                  </div>
                </React.Fragment>
              );
            })}
            {full && nowAt === rows.length && nowLine}
          </div>
          {open && scroll.scrollable && !scroll.atStart && (
            <div className={`${styles.Room__fade} ${styles["Room__fade--top"]}`} />
          )}
          {open && scroll.scrollable && !scroll.atEnd && <div className={styles.Room__fade} />}
        </div>
        {more > 0 && (
          <div className={styles.Room__moreRow}>
            <button
              type="button"
              className={styles.Room__more}
              aria-expanded={open}
              onClick={toggle}
              data-testid="quick-start-more"
            >
              {open ? "Show fewer" : `Show ${more} more`}
              {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </button>
          </div>
        )}
      </div>

      <TemplatePicker session={session} />
      {footer}
    </div>
  );
};

export default QuickStartCard;
